import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../lib/api.js';
import { defaultHealthReadTypes, HealthConnect } from '../native/health-connect.js';
import { syncHealthConnect } from '../native/health-connect-sync.js';

let lastForegroundSyncAt = 0;

async function foregroundSync(): Promise<void> {
  if (!navigator.onLine || Date.now() - lastForegroundSyncAt < 120_000) return;
  lastForegroundSyncAt = Date.now();
  const availability = await HealthConnect.getAvailability();
  if (availability.status !== 'available') return;
  const permissions = await HealthConnect.getPermissions({ recordTypes: defaultHealthReadTypes });
  if (permissions.granted.length === 0) return;
  const details = await api<{ accounts: Array<{ provider: string; status: string }> }>('/api/v1/integrations');
  const account = details.accounts.find((item) => item.provider === 'health_connect');
  if (account?.status === 'paused') return;
  await syncHealthConnect({
    bridge: HealthConnect,
    recordTypes: defaultHealthReadTypes,
    ingest: (batch) => api<{ imported: number; updated: number; deduplicated: number; deleted: number }>('/api/v1/integrations/health-connect/ingest', {
      method: 'POST',
      body: JSON.stringify({ records: batch.records, deletedExternalIds: batch.deletedIds, cursor: { recordType: batch.recordType, token: batch.token } }),
    }),
    finalize: () => api('/api/v1/integrations/health-connect/finalize', { method: 'POST', body: '{}' }).then(() => undefined),
  });
}

export function useHealthConnectForegroundSync(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;
    let listener: PluginListenerHandle | undefined;
    const run = async () => {
      await foregroundSync();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['today'] }),
        queryClient.invalidateQueries({ queryKey: ['health'] }),
        queryClient.invalidateQueries({ queryKey: ['reports'] }),
        queryClient.invalidateQueries({ queryKey: ['integrations'] }),
      ]);
    };
    void run().catch(() => undefined);
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void run().catch(() => undefined);
    }).then((handle) => { listener = handle; });
    return () => { void listener?.remove(); };
  }, [queryClient]);
}
